import axios from "axios";
import config from "../config";
const AuthService = require("../service/AuthService");

const APIUrl = config.API_BASE_URL;

// ============================================================================
// SIMPLE CACHING LAYER
// ============================================================================
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const CACHE_KEYS = {
  ACCOUNT: 'alpaca_account',
  POSITIONS: 'alpaca_positions',
  ORDERS: 'alpaca_orders',
  ACTIVITIES: 'alpaca_activities',
  PORTFOLIO_HISTORY: 'alpaca_portfolio_history'
};

// Get cached data if not expired
const getCache = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    
    if (age > CACHE_DURATION) {
      localStorage.removeItem(key); // Clean up expired cache
      return null;
    }
    
    console.log(`Using cached ${key} (${Math.round(age / 1000)}s old)`);
    return data;
  } catch (error) {
    console.log('Cache read error:', error);
    return null;
  }
};

// Save data to cache with timestamp
const setCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.log('Cache write error:', error);
  }
};

// Get cache age in seconds (for UI display)
export const getCacheAge = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const { timestamp } = JSON.parse(cached);
    return Math.round((Date.now() - timestamp) / 1000); // Return age in seconds
  } catch (error) {
    return null;
  }
};

// Clear all Alpaca caches
export const clearAllCaches = () => {
  Object.values(CACHE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  console.log('All Alpaca caches cleared');
};

/**
 * Fetches Alpaca account information
 * Returns account details: account_number, equity, buying_power, cash, status
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 */
export const fetchAlpacaAccount = async (forceRefresh = false) => {
  try {
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = getCache(CACHE_KEYS.ACCOUNT);
      if (cached) return cached;
    }
    
    // Get user from sessionStorage - it's stored as JSON string
    const username = sessionStorage.getItem('user');
    const token = AuthService.getToken();
    
    if (!username || !token) {
      throw new Error('No authentication credentials found');
    }
    
    console.log('Fetching fresh account data from API...');
    const response = await axios.post(
      `${APIUrl}/alpaca/account`,
      {
        user: {
          username: username,
        },
      },
      {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: AuthService.getToken(),
        },
      }
    );
    
    console.log("Alpaca Account:", response.data);
    
    // Save to cache
    setCache(CACHE_KEYS.ACCOUNT, response.data);
    
    return response.data;
    
  } catch (error) {
    console.log("Error fetching Alpaca account:", error);
    
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 401 || error.response.status === 403) {
        console.log("Auth failed - redirecting to login");
        AuthService.resetUserSession();
        window.location.href = "/Login";
      } else {
        console.log("API error:", error.response.status, error.response.data);
      }
    } else if (error.request) {
      // Network error
      console.log("Network error - no response received");
    } else {
      // Request setup error
      console.log("Request error:", error.message);
    }
    
    throw error;
  }
};

/**
 * Fetches Alpaca positions
 * Returns array of current positions
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 */
export const fetchAlpacaPositions = async (forceRefresh = false) => {
  try {
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = getCache(CACHE_KEYS.POSITIONS);
      if (cached) return cached;
    }
    
    const username = sessionStorage.getItem('user');
    const token = AuthService.getToken();
    
    if (!username || !token) {
      throw new Error('No authentication credentials found');
    }
    
    console.log('Fetching fresh positions data from API...');
    const response = await axios.post(
      `${APIUrl}/alpaca/positions`,
      {
        user: {
          username: username,
        },
      },
      {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: token,
        },
      }
    );
    
    console.log("Alpaca Positions:", response.data);
    
    // Save to cache
    setCache(CACHE_KEYS.POSITIONS, response.data);
    
    return response.data;
    
  } catch (error) {
    console.log("Error fetching Alpaca positions:", error);
    
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      AuthService.resetUserSession();
      window.location.href = "/Login";
    }
    
    throw error;
  }
};

/**
 * Fetches Alpaca orders
 * Returns array of orders with optional filters
 * @param {object} filters - Optional filters for orders
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 */
export const fetchAlpacaOrders = async (filters = {}, forceRefresh = false) => {
  try {
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = getCache(CACHE_KEYS.ORDERS);
      if (cached) return cached;
    }
    
    const username = sessionStorage.getItem('user');
    const token = AuthService.getToken();
    
    if (!username || !token) {
      throw new Error('No authentication credentials found');
    }
    
    console.log('Fetching fresh orders data from API...');
    const response = await axios.post(
      `${APIUrl}/alpaca/orders`,
      {
        user: {
          username: username,
        },
        ...filters,
      },
      {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: AuthService.getToken(),
        },
      }
    );
    
    console.log("Alpaca Orders:", response.data);
    
    // Save to cache
    setCache(CACHE_KEYS.ORDERS, response.data);
    
    return response.data;
    
  } catch (error) {
    console.log("Error fetching Alpaca orders:", error);
    
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      AuthService.resetUserSession();
      window.location.href = "/Login";
    }
    
    throw error;
  }
};

/**
 * Fetches Alpaca activities (fills, fees, dividends)
 * Returns array of account activities
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 */
export const fetchAlpacaActivities = async (forceRefresh = false) => {
  try {
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = getCache(CACHE_KEYS.ACTIVITIES);
      if (cached) return cached;
    }
    
    const username = sessionStorage.getItem('user');
    const token = AuthService.getToken();
    
    if (!username || !token) {
      throw new Error('No authentication credentials found');
    }
    
    console.log('Fetching fresh activities data from API...');
    const response = await axios.post(
      `${APIUrl}/alpaca/activities`,
      {
        user: {
          username: username,
        },
      },
      {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: AuthService.getToken(),
        },
      }
    );
    
    console.log("Alpaca Activities:", response.data);
    
    // Save to cache
    setCache(CACHE_KEYS.ACTIVITIES, response.data);
    
    return response.data;
    
  } catch (error) {
    console.log("Error fetching Alpaca activities:", error);
    
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      AuthService.resetUserSession();
      window.location.href = "/Login";
    }
    
    throw error;
  }
};

/**
 * Fetches Alpaca portfolio history (equity curve)
 * Returns portfolio history data
 * @param {string} period - Time period: '1W', '1M', '3M', '6M', '1A', 'all'
 * @param {string} timeframe - Time resolution: '1Min', '5Min', '15Min', '1H', '1D', '1W'
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 */
export const fetchAlpacaPortfolioHistory = async (period = '1M', timeframe = '1D', forceRefresh = false) => {
  try {
    // Create cache key that includes period and timeframe
    const cacheKey = `${CACHE_KEYS.PORTFOLIO_HISTORY}_${period}_${timeframe}`;
    
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = getCache(cacheKey);
      if (cached) return cached;
    }
    
    const username = sessionStorage.getItem('user');
    const token = AuthService.getToken();
    
    if (!username || !token) {
      throw new Error('No authentication credentials found');
    }
    
    console.log(`Fetching fresh portfolio history from API (period: ${period}, timeframe: ${timeframe})...`);
    const response = await axios.post(
      `${APIUrl}/alpaca/portfolio-history`,
      {
        user: {
          username: username,
        },
        period: period,
        timeframe: timeframe
      },
      {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: AuthService.getToken(),
        },
      }
    );
    
    console.log("Alpaca Portfolio History:", response.data);
    
    // Save to cache with period/timeframe-specific key
    setCache(cacheKey, response.data);
    
    return response.data;
    
  } catch (error) {
    console.log("Error fetching Alpaca portfolio history:", error);
    
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      AuthService.resetUserSession();
      window.location.href = "/Login";
    }
    
    throw error;
  }
};